import { FormSpy, useFieldApi } from '@data-driven-forms/react-form-renderer';
import dayjs from 'dayjs';
import { forwardRef, useEffect, useState } from 'react';
import { PatternFormat } from 'react-number-format';

import FormControl from '~/common/componentMapper/FormControl';
import FormErrorMessage from '~/common/componentMapper/FormErrorMessage';
import FormLabel from '~/common/componentMapper/FormLabel';
import DatePicker from '~/common/components/DatePicker';
import GenericTooltip from '~/common/components/GenericTooltip';
import ChakraInput from '~/common/components/Inputs/Input';
import Tooltip from '~/common/components/Tooltip';
import { formatCurrencyValue, formatDate } from '~/common/util/format';

import { validationError } from '../utils';
import CurrencyInput from './Currency';
import PercentageInput from './Percentage';
import TextArea from './TextArea';
import { handleParcelIdLabelChange } from './handlers';

const validateSsnFormat = (value: unknown): string | undefined => {
  const str = typeof value === 'string' ? value : '';
  const digits = str.replaceAll(/\D/g, '');
  return digits.length === 9
    ? undefined
    : 'SSN must be in the format 999-99-9999';
};

const SSN_ALLOWED_KEYS = new Set([
  'Backspace',
  'Delete',
  'ArrowLeft',
  'ArrowRight',
  'Tab',
  'Home',
  'End',
  '-',
]);

const formatSsn = (value: string): string => {
  const digits = value.replaceAll(/\D/g, '').slice(0, 9);
  return [digits.slice(0, 3), digits.slice(3, 5), digits.slice(5, 9)]
    .filter(Boolean)
    .join('-');
};

// maps a digit-count offset back to a caret index in the formatted string
const ssnCursorForDigitCount = (
  formatted: string,
  digitsBeforeCursor: number,
): number => {
  let seenDigits = 0;
  for (const [i, char] of [...formatted].entries()) {
    if (seenDigits === digitsBeforeCursor) return i;
    if (/\d/.test(char)) seenDigits += 1;
  }
  return formatted.length;
};

type InputSwitchProps = {
  type:
    | 'currency'
    | 'text'
    | 'number'
    | 'email'
    | 'ssn'
    | 'phone'
    | 'percentage'
    | 'date'
    | 'textarea'
    | 'hidden';
  input: {
    name: string;
    value?: any;
    checked?: boolean;
    onBlur?: (...args: any[]) => any;
    onChange?: (...args: any[]) => any;
    onFocus?: (...args: any[]) => any;
  };
  meta: {
    active?: boolean;
    dirty?: boolean;
    error?: React.ReactNode;
    invalid?: boolean;
    touched?: boolean;
    initial?: any;
  };
  label?: string;
  placeholder?: string;
  helperText?: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  onChange?: (...args: any[]) => any;
  onBlur?: (...args: any[]) => any;
  onFocus?: (...args: any[]) => any;
  onKeyDown?: (...args: any[]) => any;
  onPaste?: (...args: any[]) => any;
  sx?: any;
  fcStyle?: any;
  tabIndex?: number;
  placement?: 'top' | 'bottom' | 'left' | 'right';
  id?: string;
  maxLength?: number;
  maxWidth?: string | number;
  ariaLabel?: string;
  readOnly?: boolean;
  isInvalid?: boolean;
  showMaxLength?: boolean;
  resize?: 'none' | 'both' | 'horizontal' | 'vertical';
  role?: string;
};

const InputSwitch = forwardRef<any, InputSwitchProps>(
  ({ type, input, onChange, meta, isRequired, ...rest }, ref) => {
    const handleChange = (e) => {
      input.onChange(e);
      onChange?.(e);
    };

    const invalid =
      validationError(meta, false) || (meta.invalid && meta.touched);
    const error = invalid ? meta.error || true : false;

    switch (type) {
      case 'currency':
        return (
          <CurrencyInput
            {...input}
            meta={meta}
            {...rest}
            onChange={handleChange}
            ref={ref}
          />
        );
      case 'text':
        return (
          <ChakraInput
            {...input}
            {...rest}
            tabIndex={rest.tabIndex}
            onChange={handleChange}
            defaultValue={meta.initial}
            type="text"
            ref={ref}
          />
        );
      case 'number':
        return (
          <ChakraInput
            {...input}
            {...rest}
            tabIndex={rest.tabIndex}
            onChange={handleChange}
            defaultValue={meta.initial}
            type="number"
            ref={ref}
          />
        );
      case 'email':
        return (
          <ChakraInput
            {...input}
            {...rest}
            tabIndex={rest.tabIndex}
            onChange={handleChange}
            defaultValue={meta.initial}
            type="email"
            ref={ref}
          />
        );
      case 'ssn':
        return (
          <ChakraInput
            {...input}
            {...(rest as any)}
            tabIndex={rest.tabIndex}
            maxLength={11}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const el = e.target;
              const cursorPos = el.selectionStart ?? el.value.length;
              const digitsBeforeCursor = el.value
                .slice(0, cursorPos)
                .replaceAll(/\D/g, '').length;

              el.value = formatSsn(el.value);
              const newCursorPos = ssnCursorForDigitCount(
                el.value,
                digitsBeforeCursor,
              );
              el.setSelectionRange(newCursorPos, newCursorPos);
              handleChange(e);
            }}
            onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
              const digitCount = (e.currentTarget.value.match(/\d/g) ?? [])
                .length;
              const digitLimitReached = /^\d$/.test(e.key) && digitCount >= 9;
              if (
                digitLimitReached ||
                (!SSN_ALLOWED_KEYS.has(e.key) &&
                  !/^\d$/.test(e.key) &&
                  !e.metaKey &&
                  !e.ctrlKey)
              ) {
                e.preventDefault();
              }
              (rest as any).onKeyDown?.(e);
            }}
            onPaste={(e: React.ClipboardEvent<HTMLInputElement>) => {
              e.preventDefault();
              e.currentTarget.value = formatSsn(
                e.clipboardData.getData('text'),
              );
              input.onChange(e as any);
              onChange?.(e as any);
            }}
            defaultValue={meta.initial}
            type="text"
            ref={ref}
          />
        );
      case 'phone':
        return (
          <PatternFormat
            format="+1 (###) ###-####"
            mask="_"
            {...input}
            {...rest}
            tabIndex={rest.tabIndex}
            onChange={handleChange}
            defaultValue={meta.initial}
            customInput={ChakraInput}
            getInputRef={ref}
          />
        );
      case 'percentage':
        return (
          <PercentageInput
            {...input}
            meta={meta}
            {...rest}
            onChange={handleChange}
            ref={ref}
          />
        );
      case 'date':
        return (
          <DatePicker
            name={input.name}
            label={rest.label}
            placeholder={rest.placeholder}
            helperText={rest.helperText}
            placement={rest.placement}
            value={input.value}
            onChange={handleChange}
            onBlur={rest.onBlur}
            onFocus={rest.onFocus}
            tabIndex={rest.tabIndex}
            isRequired={isRequired}
            isDisabled={rest.isDisabled}
            error={error}
            sx={rest.sx}
            fcStyle={rest.fcStyle}
            inputRef={ref}
          />
        );
      case 'textarea':
        return (
          <TextArea
            {...input}
            {...rest}
            tabIndex={rest.tabIndex}
            onChange={handleChange}
            defaultValue={meta.initial}
            ref={ref}
          />
        );
      case 'hidden':
        return (
          <ChakraInput
            {...input}
            defaultValue={meta.initial}
            type="hidden"
            ref={ref}
          />
        );
      default:
        return (
          <ChakraInput
            {...input}
            {...rest}
            onChange={handleChange}
            tabIndex={rest.tabIndex}
            defaultValue={meta.initial}
            type="text"
            ref={ref}
          />
        );
    }
  },
);

InputSwitch.displayName = 'InputSwitch';

type InputProps = {
  type?: any;
  helperText?: string;
  formProps?: any;
  isRequired?: boolean;
  disabled?: boolean;
  readonly?: boolean;
  showIsRequired?: boolean;
  showMaxLength?: boolean;
  showToolTip?: boolean;
  tabIndex?: number;
  onChange?: (...args: any[]) => any;
  onBlur?: (...args: any[]) => any;
  onFocus?: (...args: any[]) => any;
  sx?: any;
  fcStyle?: any;
  name: string;
  label?: any;
  ariaLabel?: string;
};

const Input = forwardRef<any, InputProps>(
  ({ formProps, helperText, showIsRequired, ...props }, ref) => {
    const {
      ariaLabel,
      label: labelFromInput,
      input,
      meta,
      validateOnMount,
      type,
      isInvalid,
      hidden,
      isRequired,
      sx,
      fcStyle = {},
      placeholder = '',
      isDisabled,
      disabled,
      maxLength,
      resize,
      showMaxLength,
      readonly,
      placement,
      maxWidth,
      role,
      showToolTip,
      onChange,
      onBlur,
      onFocus,
    } = useFieldApi({
      ...props,
      ...(props.type === 'ssn' ? { validate: [validateSsnFormat] as any } : {}),
    });
    const { name } = props;

    const [label, setLabel] = useState(labelFromInput);
    const [isEditing, setIsEditing] = useState(false);

    const invalid =
      validationError(meta, validateOnMount) || (meta.invalid && meta.touched);
    const errorText =
      ((meta.touched || validateOnMount) && meta.warning) ||
      isInvalid ||
      meta.error;

    // MUI components will have labels and form error texts in the component
    // Put here to skip placing them separately in the input component.
    const isMui = ['date'].includes(type);

    const showError = !(errorText === true || errorText === false);
    const valueLength = input.value?.toString().length;
    const tabIndex = isDisabled ? -1 : 0;
    const cutOffLength = 5;
    const showInputWithToolTip =
      showToolTip && valueLength > cutOffLength && !isEditing;
    const backgroundColor = sx?.backgroundColor ? sx.backgroundColor : 'white';

    const handleOnBlur = (e) => {
      input.onBlur();
      onBlur?.(e);
      setIsEditing(false);
    };

    const handleOnFocus = (e) => {
      setIsEditing(true);
      onFocus?.(e);
    };

    useEffect(() => {
      if (!input) {
        return;
      }

      if (type === 'date') {
        const date = dayjs(input.value);
        input.onChange(date.isValid() ? date.format('MM/DD/YYYY') : null);
        return;
      }

      input.onChange(`${input.value}`);
      // eslint-disable-next-line @eslint-react/exhaustive-deps
    }, []);

    if (showIsRequired === undefined && !label) {
      showIsRequired = false;
    }

    if (isMui) {
      return (
        <InputSwitch
          sx={{ ...sx, backgroundColor }}
          fcStyle={fcStyle}
          tabIndex={tabIndex}
          type={hidden ? 'hidden' : type}
          input={input}
          id={name}
          label={label}
          placeholder={placeholder}
          helperText={helperText}
          meta={meta}
          isRequired={isRequired && showIsRequired}
          isDisabled={isDisabled}
          maxLength={maxLength}
          placement={placement}
          maxWidth={maxWidth}
          onFocus={handleOnFocus}
          onBlur={handleOnBlur}
          onChange={onChange}
          ref={ref}
          ariaLabel={ariaLabel}
        />
      );
    }

    const getLabel = () => {
      if (type === 'currency') {
        return formatCurrencyValue(input.value.toString());
      }
      if (type === 'date') {
        return formatDate(input.value.toString());
      }
      return input.value;
    };

    return (
      <FormSpy subscription={{ values: true }}>
        {({ values }) => {
          const isFirstParcelInput = labelFromInput?.includes('Parcel ID');
          isFirstParcelInput &&
            handleParcelIdLabelChange({ values, isFirstParcelInput, setLabel });
          return (
            <FormControl
              sx={{
                marginBottom: formProps?.mb ? '1rem' : 0,
                ...fcStyle,
              }}
              defaultValue={meta.initial}
              hidden={type === 'hidden'}
              isRequired={
                isRequired && (showIsRequired || showIsRequired === undefined)
              }
              isInvalid={!!invalid && !isDisabled && showError}
              role={role || 'group'}
            >
              <FormLabel m={0} htmlFor={name}>
                {label}
                {helperText && <GenericTooltip label={helperText} />}
              </FormLabel>
              {showInputWithToolTip ? (
                <Tooltip
                  placement="top"
                  textAlign="center"
                  color="black"
                  bg="gray.200"
                  borderRadius="2px"
                  fontSize="12px"
                  fontWeight="normal"
                  p={2}
                  label={getLabel()}
                >
                  <span role="tooltip" aria-label={label ?? 'Information'}>
                    <InputSwitch
                      sx={{
                        ...sx,
                        backgroundColor,
                      }}
                      tabIndex={tabIndex}
                      type={hidden ? 'hidden' : type}
                      input={input}
                      id={name}
                      placeholder={placeholder}
                      meta={meta}
                      readOnly={readonly}
                      isDisabled={isDisabled || disabled}
                      maxLength={maxLength}
                      showMaxLength={showMaxLength}
                      resize={resize}
                      placement={placement}
                      maxWidth={maxWidth}
                      onFocus={handleOnFocus}
                      onBlur={handleOnBlur}
                      onChange={onChange}
                      ref={ref}
                      ariaLabel={ariaLabel}
                      role={role}
                    />
                  </span>
                </Tooltip>
              ) : (
                <InputSwitch
                  sx={{ ...sx, backgroundColor }}
                  tabIndex={tabIndex}
                  type={hidden ? 'hidden' : type}
                  input={input}
                  id={name}
                  placeholder={placeholder}
                  meta={meta}
                  readOnly={readonly}
                  isDisabled={isDisabled || disabled} // Need to standardize this
                  isInvalid={invalid}
                  maxLength={maxLength}
                  showMaxLength={showMaxLength}
                  resize={resize}
                  placement={placement}
                  maxWidth={maxWidth}
                  onFocus={handleOnFocus}
                  onBlur={handleOnBlur}
                  onChange={onChange}
                  ref={ref}
                  ariaLabel={ariaLabel}
                  role={role}
                />
              )}
              {showError && <FormErrorMessage>{errorText}</FormErrorMessage>}
            </FormControl>
          );
        }}
      </FormSpy>
    );
  },
);

Input.displayName = 'Input';

export default Input;
